import { useCallback, useEffect } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'

import { useAIStore } from '@/stores/ai-store'
import { toastError, toastSuccess } from '@/stores'
import { useQuoteDraftStore } from '@/stores/quote-draft-store'
import type { AIConfig } from '@/modules/ai/types'

const QUERY_KEY = ['ai-commercial'] as const

/**
 * useAI — commercial assistant hook.
 * Recommendations only; never applies quote changes automatically.
 */
export function useAI() {
  const queryClient = useQueryClient()
  const draftItems = useQuoteDraftStore((s) => s.items)

  const hydrate = useAIStore((s) => s.hydrate)
  const ready = useAIStore((s) => s.ready)
  const config = useAIStore((s) => s.config)
  const quoteInput = useAIStore((s) => s.quoteInput)
  const analysis = useAIStore((s) => s.analysis)
  const insights = useAIStore((s) => s.insights)
  const description = useAIStore((s) => s.description)
  const observations = useAIStore((s) => s.observations)
  const history = useAIStore((s) => s.history)
  const disclaimer = useAIStore((s) => s.disclaimer)
  const loading = useAIStore((s) => s.loading)
  const lastError = useAIStore((s) => s.lastError)
  const loadFromDraft = useAIStore((s) => s.loadFromDraft)
  const loadSample = useAIStore((s) => s.loadSample)
  const saveConfigStore = useAIStore((s) => s.saveConfig)
  const analyzeQuoteStore = useAIStore((s) => s.analyzeQuote)
  const generateDescriptionStore = useAIStore((s) => s.generateDescription)
  const generateObservationsStore = useAIStore((s) => s.generateObservations)

  const bootstrap = useQuery({
    queryKey: QUERY_KEY,
    queryFn: async () => {
      hydrate()
      loadFromDraft(useQuoteDraftStore.getState().items)
      return true
    },
  })

  useEffect(() => {
    if (!ready) hydrate()
  }, [ready, hydrate])

  const syncDraft = useCallback(() => {
    loadFromDraft(draftItems)
    toastSuccess('Orçamento sincronizado', `${draftItems.length || 'demo'} item(ns)`)
  }, [draftItems, loadFromDraft])

  const analyzeQuote = useCallback(async () => {
    await analyzeQuoteStore()
    void queryClient.invalidateQueries({ queryKey: QUERY_KEY })
    const error = useAIStore.getState().lastError
    if (error) toastError('Análise', error)
    else toastSuccess('Análise concluída', 'Recomendações prontas — confirme antes de aplicar')
  }, [analyzeQuoteStore, queryClient])

  const generateDescription = useCallback(async () => {
    await generateDescriptionStore()
    const error = useAIStore.getState().lastError
    if (error) toastError('Descrição', error)
    else toastSuccess('Descrição técnica', 'Texto gerado (somente recomendação)')
  }, [generateDescriptionStore])

  const generateObservations = useCallback(async () => {
    await generateObservationsStore()
    const error = useAIStore.getState().lastError
    if (error) toastError('Observações', error)
    else toastSuccess('Observações comerciais', 'Texto gerado (somente recomendação)')
  }, [generateObservationsStore])

  const saveConfig = useCallback(
    (next: AIConfig) => {
      saveConfigStore(next)
      toastSuccess('Configurações IA', next.model)
    },
    [saveConfigStore],
  )

  return {
    ready,
    config,
    saveConfig,
    quoteInput,
    analysis,
    insights,
    description,
    observations,
    history,
    disclaimer,
    loading,
    lastError,
    draftCount: draftItems.length,
    syncDraft,
    loadSample,
    analyzeQuote,
    generateDescription,
    generateObservations,
    isLoading: bootstrap.isLoading || loading,
  }
}
